import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { GET } from "@/services/activity-requests";
import { LessonLevel, Question } from "@/types/activity";
import { toast } from "sonner";
import { removeMultipleKeys } from "@/utils/function";
import { tokenRefresh } from "@/redux/Refresh";

interface ActivityLessonData {
  [key: string]: any;
}

interface ActivityLessonState {
  data: ActivityLessonData | null;
  loading: boolean;
  error: string | null;
}

interface FetchActivityLessonArgs {
  token: string;
  level_id: string | undefined;
  language: string | undefined;
}

// Initial state with proper typing
const initialState: ActivityLessonState = {
  data: null,
  loading: false,
  error: null,
};

export const activityLessonFetch = createAsyncThunk<
  LessonLevel,
  FetchActivityLessonArgs,
  { rejectValue: string }
>("activity-lesson", async ({ token, level_id, language }, { rejectWithValue }) => {
  try {
    const { response } = await GET(
      `/interactive-lessons/${language}/levels/${level_id}/lessons`,
      token
    );
    return response?.data?.result;
  } catch (err: unknown) {
    const error = err as any;
    if (error.response?.status === 401) {
      removeMultipleKeys(["pluri_token", "pluri_refresh_token", "user_detail"]);
      toast.error("Session expired. Please log in again.", {
        id: "session-expired-toast",
        richColors: true,
      });
      window.location.href = "/login";
    }
    const errorMessage =
      error.response?.data?.message || "An error occurred while fetching the profile picture";
    return rejectWithValue(errorMessage);
  }
});

const activityLessonSlice = createSlice({
  name: "activityLesson",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(activityLessonFetch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        activityLessonFetch.fulfilled,
        (state, action: PayloadAction<ActivityLessonData>) => {
          state.data = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(
        activityLessonFetch.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Unknown error";
        }
      );
  },
});

export default activityLessonSlice.reducer;
